import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { distinctUntilChanged, Observable, of, takeWhile } from 'rxjs';

import {
  BaseStyleClassesDirective,
  DocumentSectionService,
  markFormGroupsTouched,
  PortalId,
} from '@gk/gk-modules';
import { InvestorDetailsFormService } from '../../services/investor-details-form/investor-details-form.service';
import { InvestorDetailsComponent } from '../../shared/investor-details/investor-details.component';
import { InvestorDetails } from '../../shared/investor-details/investor-details.model';

export type AtLeastOneDetailsType =
  | { Details: string; DetailsArray?: string; DetailsCheckbox?: string }
  | { Details?: string; DetailsArray: string; DetailsCheckbox?: string };

@Component({
  selector: 'app-multi-person-data',
  templateUrl: './multi-person-data.component.html',
  standalone: false,
})
export class MultiPersonDataComponent
  extends BaseStyleClassesDirective
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  private isAlive = true;
  private _pendingDetails: InvestorDetails | InvestorDetails[] | null = null;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent?: InvestorDetailsComponent;
  @ViewChildren(InvestorDetailsComponent)
  investorDetailsComponents?: QueryList<InvestorDetailsComponent>;
  @Input() portalId: PortalId;
  @Input() formGroup: FormGroup;
  @Input() submitted: boolean;
  @Input() controlName: AtLeastOneDetailsType;
  @Input() multiple = false;
  @Input() proxyTypeFormControlVisible = false;
  @Input() addressFormGroupBasedOnSelectControls = false;
  @Input() title = 'PERSON_DETAILS';
  @Input() singleElementTitle = 'PERSON_DETAILS';
  @Input() addButtonText = 'ADD_PERSON';
  @Input() removeButtonText = 'REMOVE';
  @Input() validationMessage = 'FIELDS_REQUIRED';
  @Input() infoText = 'PERSON_DETAILS_INFO';
  @Input() onlinePaymentInfoText = 'PERSON_DETAILS_INFO_ONLINE_PAYMENT';
  @Input() idPrefix = 'person-';
  @Input() infoVisible = true;
  @Input() isPeselVisible: boolean;

  detailsVisible = false;
  attachmentsSelected = false;

  constructor(
    private investorDetailsFormService: InvestorDetailsFormService,
    private formBuilder: FormBuilder,
    public documentSectionService: DocumentSectionService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToDetailsCheckboxFormControl();
    this.watchAttachmentsSelected();
  }

  ngAfterViewInit(): void {
    if (this.investorDetailsComponents) {
      this.investorDetailsComponents.changes.subscribe(() => {
        this.trySetPendingAddresses();
      });
      this.trySetPendingAddresses();
    }
  }

  private trySetPendingAddresses(): void {
    if (!this._pendingDetails) return;

    if (
      this.multiple &&
      this.investorDetailsComponents &&
      this.investorDetailsComponents.length
    ) {
      this.investorDetailsComponents.forEach((component, i) => {
        const value = Array.isArray(this._pendingDetails)
          ? this._pendingDetails[i].addressSelect
          : this._pendingDetails.addressSelect;
        if (value) {
          component.setAddressSelectFormGroupValue(value);
        }
      });
      this._pendingDetails = null;
    } else if (!this.multiple && this.investorDetailsComponent) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        Array.isArray(this._pendingDetails)
          ? this._pendingDetails[0].addressSelect
          : this._pendingDetails.addressSelect,
      );
      this._pendingDetails = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const submittedChange = changes['submitted'];
    if (submittedChange && submittedChange.currentValue) {
      const detailsFormGroup = this.getDetailsFormGroup();
      if (!detailsFormGroup) {
        return;
      }
      markFormGroupsTouched(detailsFormGroup);
    }
  }

  watchAttachmentsSelected(): void {
    const selected$ = this.documentSectionService.getProxyAttachmentsSelected(
      this.portalId,
    );
    selected$
      .pipe(
        takeWhile(() => this.isAlive),
        distinctUntilChanged(),
      )
      .subscribe((selected) => {
        this.attachmentsSelected = selected;
        this.getDetailsCheckboxFormControl().setValue(selected);
      });
  }

  subscribeToDetailsCheckboxFormControl(): void {
    this.getDetailsCheckboxFormControl()
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .subscribe((checked) => {
        this.toggleFormVisibilityByChecked(checked);
      });
  }

  toggleFormVisibilityByChecked(checked: boolean): void {
    const controlName = this.getDetailsControlName();
    const hasDetailsFormGroup = this.formGroup.contains(
      this.getDetailsControlName(),
    );
    if (checked && !hasDetailsFormGroup) {
      this.formGroup.addControl(controlName, this.initDetailsControl());
    } else if (hasDetailsFormGroup && !this.attachmentsSelected) {
      this.formGroup.removeControl(controlName);
    }
    this.setDetailsVisibility(checked);
  }

  getDetailsControlName(): string {
    return this.multiple
      ? this.controlName.DetailsArray
      : this.controlName.Details;
  }

  initDetailsControl(): UntypedFormGroup | UntypedFormArray {
    const detailsFormGroup = this.createDetailsFormGroup();

    return this.multiple
      ? this.formBuilder.array([detailsFormGroup])
      : detailsFormGroup;
  }

  addDetailsFormGroup(value?: InvestorDetails): void {
    const detailsFormGroup = this.createDetailsFormGroup();
    const arr = this.getDetailsFormArray();
    if (arr) {
      arr.push(detailsFormGroup);
      if (value) {
        detailsFormGroup.patchValue(value);
      }
    }
  }

  removeDetailsFormGroup(index: number): void {
    const arr = this.getDetailsFormArray();
    if (arr && arr.length > index) arr.removeAt(index);
  }

  createDetailsFormGroup(): UntypedFormGroup {
    return this.investorDetailsFormService.getFormGroup(
      false,
      undefined,
      undefined,
      false,
      undefined,
      this.addressFormGroupBasedOnSelectControls,
      undefined,
      this.proxyTypeFormControlVisible,
    );
  }

  getDetailsFormArray(): UntypedFormArray {
    return this.formGroup.get(
      this.controlName.DetailsArray,
    ) as UntypedFormArray;
  }

  getDetailsCheckboxFormControl(): UntypedFormControl {
    return this.formGroup.get(
      this.controlName.DetailsCheckbox,
    ) as UntypedFormControl;
  }

  isDetailsInvalidAndValidated(): boolean {
    const detailsFormGroup = this.getDetailsFormGroup();
    return (
      detailsFormGroup &&
      !detailsFormGroup.valid &&
      this.detailsVisible &&
      this.submitted
    );
  }

  getDetailsFormGroup(index?: number): UntypedFormGroup {
    if (this.multiple && index !== undefined) {
      return this.getDetailsFormArray().at(index) as UntypedFormGroup;
    }
    return this.formGroup.get(this.controlName.Details) as UntypedFormGroup;
  }

  isDetailsVisible(): boolean {
    const shouldBeVisible =
      this.detailsVisible || (this.submitted && this.attachmentsSelected);

    if (shouldBeVisible && !this.detailsVisible) {
      this.setDetailsVisibility(true);
      this.getDetailsCheckboxFormControl().setValue(true);
    }

    return shouldBeVisible;
  }

  setDetailsVisibility(visible: boolean): void {
    this.detailsVisible = visible;
  }

  askForPostOfficeWhenPostalCodeIsNotFromDictionary(
    customPostOfficeModalHeaderInfo?: string,
  ): Observable<boolean> {
    return this.investorDetailsComponent
      ? this.investorDetailsComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(
          customPostOfficeModalHeaderInfo,
        )
      : of(true);
  }

  setFormGroupValue(data: InvestorDetails | InvestorDetails[]): void {
    const isArray = Array.isArray(data);
    if (this.multiple && isArray) {
      this.getDetailsFormArray()?.clear();
      data.forEach((element) => this.addDetailsFormGroup(element));

      if (
        this.investorDetailsComponents &&
        this.investorDetailsComponents.length
      ) {
        this.investorDetailsComponents.forEach((component, i) => {
          const value = data[i];
          if (value) {
            component.setAddressSelectFormGroupValue(value.addressSelect);
          }
        });
        this._pendingDetails = null;
      } else {
        this._pendingDetails = data;
      }
    } else if (!isArray) {
      if (this.investorDetailsComponent) {
        this.investorDetailsComponent.setAddressSelectFormGroupValue(
          data.addressSelect,
        );
        this._pendingDetails = null;
      } else {
        this._pendingDetails = data;
      }
    }
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
